import {
  ChangeProjectionService,
  CounterDeltas,
  LabelingService,
  Roi,
  RoiChange,
  type LabelCategory,
} from '@analysis/domain';
import { type RoiChangesRepository, type RoiRepository } from '../../ports';

export interface AddLabelsPayload {
  rois: Roi[];
  labels: string[];
  categories: Record<string, LabelCategory>;
}

export interface AddLabelsResult {
  changes: Map<string, RoiChange | null>;
  updatedRois: Map<string, Roi>;
  counterDeltas: CounterDeltas;
}

export class AddLabelsUseCase {
  constructor(private readonly roiChanges: RoiChangesRepository, private readonly rois: RoiRepository) {}

  public async execute(payload: AddLabelsPayload) {
    const { rois, labels, categories } = payload;

    const roiIds = rois.map((roi) => roi.id);

    const updatedRois: Map<string, Roi> = new Map();
    const changes: Map<string, RoiChange | null> = new Map();
    const allDeltas: CounterDeltas[] = [];

    const previousChanges = await this.roiChanges.getByIds(roiIds);

    const partialUuids = rois
      .filter((r) => {
        if (r.additionalLabelsIncluded || !r.uuid) return false;
        const prior = previousChanges.get(r.id);
        if (prior?.isNew) return false;
        return true;
      })
      .map((r) => r.uuid as string);
    const baselines = partialUuids.length > 0 ? await this.rois.batchGetByIds(partialUuids) : new Map<string, Roi>();

    const hydratedRois = rois.map((roi) => {
      if (roi.additionalLabelsIncluded || !roi.uuid) return roi;
      const prior = previousChanges.get(roi.id);
      if (prior?.isNew) return prior.snapshot ?? roi;
      const baseline = baselines.get(roi.uuid);
      if (!baseline) return roi;
      return prior ? ChangeProjectionService.applyChanges(baseline, prior) : baseline;
    });

    const timestamp = Date.now();

    for (const roi of hydratedRois) {
      const previousChange = previousChanges.get(roi.id);
      const result = LabelingService.addLabels(roi, labels, categories);
      const totalChanges = ChangeProjectionService.mergeChanges({
        changes: previousChange ? [previousChange, result.changes] : [result.changes],
        isNew: previousChange?.isNew ?? false,
      });

      allDeltas.push(
        ChangeProjectionService.computeCounterDeltas(
          Array.from(roi.labels.keys()),
          Array.from(result.roi.labels.keys()),
          result.changes.labelsAdded,
          result.changes.labelsRemoved
        )
      );

      updatedRois.set(roi.id, result.roi);

      changes.set(
        roi.id,
        totalChanges === null
          ? null
          : {
              id: roi.id,
              sampleId: roi.sampleId,
              assetId: roi.assetId,
              creatorId: roi.creatorId,
              timestamp,
              isNew: previousChange?.isNew ?? false,
              labelsAdded: totalChanges.labelsAdded,
              labelsRemoved: totalChanges.labelsRemoved,
              snapshot: previousChange?.isNew ? result.roi : previousChange?.snapshot,
            }
      );
    }

    await this.roiChanges.upsertRoiChanges(changes);

    return { changes, updatedRois, counterDeltas: ChangeProjectionService.mergeCounterDeltas(allDeltas) };
  }
}
